import React from 'react';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import { SxProps, Theme } from '@mui/material/styles';
import { useRegisterFilterActivity } from '../FilterDrawer/FilterActivityContext';

interface SearchFilterProps {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  placeholder?: string;
  sx?: SxProps<Theme>;
  /**
   * When provided, this filter self-reports its active state to the
   * surrounding `FilterDrawerContainer`'s activity registry. Active means
   * the input has a non-empty (trimmed) value.
   */
  filterId?: string;
  /**
   * Optional override for the auto-derived active state. When omitted, the
   * filter reports `value.trim().length > 0`.
   */
  isActive?: boolean;
  /**
   * Optional callback fired when the user clicks the X (clear) icon.
   * Distinct from a plain `onChange('')` because it captures an explicit
   * clear action (useful for analytics).
   */
  onClear?: () => void;
}

/**
 * Reusable search input with a search icon (empty) / clear icon (has value).
 *
 * Controlled — updates parent on every keystroke. The parent is responsible
 * for debouncing the value before passing it to the filter pipeline.
 */
const SearchFilter: React.FC<SearchFilterProps> = ({
  value,
  onChange,
  placeholder,
  sx,
  label,
  filterId = 'search',
  isActive,
  onClear,
}) => {
  useRegisterFilterActivity(filterId, isActive ?? value.trim().length > 0);

  const handleClearClick = () => {
    if (!value) return;
    onChange('');
    onClear?.();
  };

  return (
    <TextField
      variant="filled"
      label={label}
      placeholder={placeholder}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid="filter-search"
      slotProps={{
        input: {
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                size="small"
                aria-label={value ? 'Clear search' : 'Search'}
                onClick={handleClearClick}
                edge="end"
                disableRipple={!value}
                sx={{ cursor: value ? 'pointer' : 'default' }}
              >
                {value ? (
                  <CloseIcon fontSize="small" />
                ) : (
                  <SearchIcon
                    fontSize="small"
                    sx={{ color: 'action.active' }}
                  />
                )}
              </IconButton>
            </InputAdornment>
          ),
        },
        htmlInput: { 'aria-label': placeholder },
      }}
      sx={{
        width: '100%',
        ...sx,
      }}
    />
  );
};

export default SearchFilter;
